import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import BillableGeneralCard from './billableGeneralCard';
import { usePage } from '../../context/PageContext';
import { useFetchMatterById } from '../../hooks/useFetchMatterById';
import { IMatter } from '../../interface/matter';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { EntryEntity } from '../../constants/entries';
import EntriesView from '../matterDetail/entries/entriesView';
import PartiesListing from '../parties/partiesListing';
import { NotesEntity } from '../../constants/notes';
import Notes from '../conflictSearch/notes';
import { MyInvoicingDetailTab, MyInvoicingTab } from '../../interface/invoice';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsPage } from '../../interface/analytics';
import CustomTabs from '../customTabs/customTabs';
import DetailScrollArea from '../detailScrollArea/detailScrollArea';
import { BILLABLE_MATTER_QUERY } from '../../constants/invoice';

const BillableMatterDetailTabView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const { goBack } = useNavigationStack();
  const { setTitle } = usePage();
  const { trackPage } = useAnalytics();
  const fromTab = location.state?.fromTab || MyInvoicingDetailTab.general;
  const [selectedTab, setSelectedTab] = useState<string>(fromTab);
  const { data: billableMatter, isLoading } = useFetchMatterById({
    id,
    ...BILLABLE_MATTER_QUERY,
  });

  useEffect(() => {
    trackPage(AnalyticsPage.MATTER_DETAIL);
  }, [trackPage]);
  const handleBack = () => {
    goBack({ fromTab: MyInvoicingTab.billableMatters });
  };
  useEffect(() => {
    if (billableMatter && !isLoading) {
      setTitle(
        <AddEditTransactionHeader
          title={billableMatter.subject ?? ''}
          subTitle={t('matter:myBillableMatter')}
          handleBack={handleBack}
        />,
      );
    }
  }, [billableMatter, isLoading]);

  const items = useMemo(
    () => [
      {
        key: MyInvoicingDetailTab.general,
        label: t('common:tab.general'),
        children: (
          <DetailScrollArea>
            <BillableGeneralCard
              billableMatter={billableMatter as IMatter}
              key={billableMatter?.id}
              isLoading={isLoading}
            />
          </DetailScrollArea>
        ),
      },
      {
        key: MyInvoicingDetailTab.billableEntries,
        label: t('common:tab.billableEntries'),
        children: (
          <EntriesView
            entity={EntryEntity.MATTER}
            invoicingPeriod={[
              billableMatter?.periodStartDate ?? null,
              billableMatter?.periodEndDate ?? null,
            ]}
            matter={billableMatter}
            isBillableMatter={true}
          />
        ),
      },
      {
        key: MyInvoicingDetailTab.payers,
        label: t('common:tab.payers'),
        children: (
          <PartiesListing
            isBillable
            type={EntryEntity.MATTER}
            noDataHeader={t('preliminaryInvoice:noPayers')}
          />
        ),
      },
      {
        key: MyInvoicingDetailTab.notes,
        label: t('common:tab.notes'),
        children: (
          <Notes entitySubject={billableMatter?.subject ?? ''} entity={NotesEntity.MATTER} />
        ),
      },
    ],
    [billableMatter, t],
  );
  return (
    <CustomTabs variant="pill" activeKey={selectedTab} onChange={setSelectedTab} items={items} />
  );
};

export default BillableMatterDetailTabView;
